import { Component, ErrorInfo, ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import { Sentry } from '@/lib/sentry';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-sm w-full text-center space-y-6">
          <div className="w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto">
            <RefreshCw size={28} className="text-destructive" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-bold font-display">Une erreur est survenue</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              L'application a rencontré un problème inattendu. Vos données locales sont préservées.
            </p>
          </div>
          {import.meta.env.DEV && (
            <pre className="text-left text-xs bg-muted p-3 rounded-xl overflow-auto max-h-32 text-destructive">
              {error.message}
            </pre>
          )}
          <button
            onClick={() => window.location.reload()}
            className="w-full px-5 py-2.5 bg-primary text-primary-foreground rounded-xl font-semibold text-sm hover:bg-primary/90 transition-colors"
          >
            Recharger l'application
          </button>
        </div>
      </div>
    );
  }
}
